/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import { useSelector } from 'react-redux';
import { VideoRenderMode } from '@volcengine/rtc';
import { useEffect } from 'react';
import { RootState } from '@/store';
import { useDeviceState, useScene } from '@/lib/useCommon';
import RtcClient from '@/lib/RtcClient';

import styles from './index.module.less';
import UserTag from '@/components/UserTag';
import UserAvatar from '@/assets/img/userAvatar.png';
import CameraCloseNoteSVG from '@/assets/img/CameraCloseNote.svg';
import ScreenCloseNoteSVG from '@/assets/img/ScreenCloseNote.svg';
import { LocalFullID, RemoteFullID } from '@/components/FullScreenCard';

const LocalVideoID = 'local-video-player';
const LocalScreenID = 'local-screen-player';
const RemoteVideoID = 'remote-video-player';

type CameraAreaProps = React.HTMLAttributes<HTMLDivElement> & {
  fillContainer?: boolean;
};

function CameraArea(props: CameraAreaProps) {
  const { className, fillContainer = false, ...rest } = props;
  const room = useSelector((state: RootState) => state.room);
  const { isFullScreen, scene } = room;
  const { isVision, isScreenMode, botName } = useScene();
  const { isVideoPublished, isScreenPublished } = useDeviceState();
  const isRemoteVideoPublished =
    room.remoteUsers.find((user) => user.username === botName)?.publishVideo ?? false;
  const localPlayerId = fillContainer
    ? isScreenMode
      ? LocalScreenID
      : LocalVideoID
    : isFullScreen
    ? LocalFullID
    : isScreenMode
    ? LocalScreenID
    : LocalVideoID;

  const setVideoPlayer = () => {
    RtcClient.removeLocalVideoPlayer(room.localUser.username!);
    if (isVideoPublished || isScreenPublished) {
      RtcClient.setLocalVideoPlayer(
        room.localUser.username!,
        localPlayerId,
        isScreenPublished,
        isScreenMode ? VideoRenderMode.RENDER_MODE_FILL : VideoRenderMode.RENDER_MODE_HIDDEN
      );
      if (isRemoteVideoPublished) {
        RtcClient.setRemoteVideoPlayer(
          botName,
          isFullScreen ? RemoteVideoID : RemoteFullID
        );
      }
    }
  };

  useEffect(() => {
    setVideoPlayer();
  }, [fillContainer, isVideoPublished, isScreenPublished, isScreenMode, isFullScreen, isVision]);

  return (
    <div
      className={[styles['camera-wrapper'], className].filter(Boolean).join(' ')}
      style={
        fillContainer
          ? {
              position: 'absolute',
              inset: 0,
              top: 0,
              right: 0,
              width: '100%',
              height: '100%',
              border: 0,
              borderRadius: 'inherit',
              overflow: 'hidden',
              background: 'transparent',
              justifyContent: 'flex-start',
              alignItems: 'stretch',
              pointerEvents: 'none',
            }
          : undefined
      }
      {...rest}
    >
      <UserTag
        name={fillContainer ? '你' : isFullScreen ? scene : '我'}
        compact={fillContainer}
        className={styles.userTag}
      />
      <div
        id={LocalVideoID}
        className={`${styles['camera-player']} ${
          isVideoPublished && !isScreenMode ? '' : styles['camera-player-hidden']
        }`}
        style={fillContainer ? { height: '100%', pointerEvents: 'none' } : undefined}
      />
      <div
        id={LocalScreenID}
        className={`${styles['camera-player']} ${
          isScreenPublished && isScreenMode ? '' : styles['camera-player-hidden']
        }`}
        style={fillContainer ? { height: '100%', pointerEvents: 'none' } : undefined}
      />
      <div
        id={RemoteVideoID}
        className={`${styles['camera-player']} ${
          isFullScreen && isRemoteVideoPublished ? '' : styles['camera-player-hidden']
        }`}
        style={
          fillContainer
            ? { position: 'absolute', height: '100%', pointerEvents: 'none' }
            : { position: 'absolute' }
        }
      />
      <div
        className={`${styles['camera-placeholder']} ${
          isVideoPublished || isScreenPublished ? styles['camera-player-hidden'] : ''
        }`}
        style={fillContainer ? { height: '100%', pointerEvents: 'none' } : undefined}
      >
        <img
          src={isScreenMode ? ScreenCloseNoteSVG : isVision ? CameraCloseNoteSVG : UserAvatar}
          alt="close"
          className={styles['camera-placeholder-close-note']}
        />

        {isFullScreen ? null : (
          <div>
            {isScreenMode ? (
              <>
                <div>屏幕共享已关闭</div>
                <div>开启后即可发送实时屏幕画面</div>
              </>
            ) : isVision ? (
              <>
                <div>摄像头已关闭</div>
                <div>打开后即可发送实时画面给 AI</div>
              </>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

export default CameraArea;
